import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'
import { iamApi } from '@/api/client'
import type { PaginatedResponse, SiteResponse } from '@/api/types'
import { Globe, CheckCircle, FileEdit, Wrench } from 'lucide-react'

export function DashboardPage() {
  const { t } = useTranslation()
  const { activeTenantId } = useAuth()
  const [sites, setSites] = useState<SiteResponse[]>([])
  const [total, setTotal] = useState(0)

  useEffect(() => {
    iamApi.get<PaginatedResponse<SiteResponse>>('/sites', { params: { limit: 5 } })
      .then(({ data }) => {
        setSites(data.data || [])
        setTotal(data.total || 0)
      })
      .catch(() => {})
  }, [activeTenantId])

  const activeCount = sites.filter(s => s.status === 'active').length
  const draftCount = sites.filter(s => s.status === 'draft').length
  const maintenanceCount = sites.filter(s => s.status === 'maintenance').length

  const stats = [
    { label: t('dashboard.totalSites'), value: total, icon: Globe, color: 'text-primary' },
    { label: t('dashboard.activeSites'), value: activeCount, icon: CheckCircle, color: 'text-green-600' },
    { label: t('dashboard.draftSites'), value: draftCount, icon: FileEdit, color: 'text-yellow-600' },
    { label: t('dashboard.maintenanceSites'), value: maintenanceCount, icon: Wrench, color: 'text-orange-600' },
  ]

  return (
    <div>
      <h1 className="text-2xl font-bold text-foreground mb-6">{t('dashboard.title')}</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">{stat.label}</span>
              <stat.icon className={`h-5 w-5 ${stat.color}`} />
            </div>
            <p className="text-3xl font-bold text-foreground">{stat.value}</p>
          </div>
        ))}
      </div>
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.recentSites')}</h2>
        {sites.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('sites.noSites')}</p>
        ) : (
          <div className="bg-card border border-border rounded-xl divide-y divide-border">
            {sites.map((site) => (
              <Link
                key={site.id}
                to={`/sites/${site.id}`}
                state={{ tenantId: site.tenant_id }}
                className="flex items-center justify-between px-5 py-3 hover:bg-secondary transition-colors"
              >
                <div>
                  <p className="font-medium text-foreground">{site.name}</p>
                  <p className="text-sm text-muted-foreground">{site.domain || site.slug}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  site.status === 'active' ? 'bg-green-100 text-green-700' :
                  site.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                  site.status === 'maintenance' ? 'bg-orange-100 text-orange-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {site.status}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
